import React, { useState, useEffect, useRef } from 'react';
import { Box, Typography, CircularProgress, Backdrop, IconButton, InputAdornment, Switch } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { useNotifications } from '@core/hooks/useNotifications';
import EditorActions from '@/components/EditorActions.tsx';
import { required, notNegative } from '@core/utils/validation';
import { FormContainer, WcnCheckbox } from '@/components';
import { FormContainerRef } from '@/components/form/FormContainer.tsx';
import FormTextField from '@/components/form/FormTextField.tsx';
import { Organisation, OrganisationInput, LICENSE_OPTIONS } from '@/services/organisationService';
import { ActiveLicenses } from '@/core/types';

interface OrganisationEditorProps {
    item: Organisation;
    isSaving: boolean;
    onSave: (item: OrganisationInput) => void;
    onDelete: (item: Organisation) => void;
    onCancel: () => void;
}

const OrganisationEditor: React.FC<OrganisationEditorProps> = ({ item, isSaving, onSave, onDelete, onCancel }) => {
    const { t } = useTranslation();
    const { notifyWarning } = useNotifications();
    const [formData, setFormData] = useState<OrganisationInput>({
        id: item.id,
        organisationId: item.organisationId,
        name: item.name,
        activeLicenses: item.activeLicenses,
        recycleBinRetentionDays: item.recycleBinRetentionDays,
        deviceDisconnectNotificationRecipients: item.deviceDisconnectNotificationRecipients,
        ftpSettings: item.ftpSettings,
        fastLanerOrgId: item.fastLanerOrgId
    });
    const [selectedLicenses, setSelectedLicenses] = useState<ActiveLicenses[]>([]);
    const [showPassword, setShowPassword] = useState(false);
    const [formValid, setFormValid] = useState(false);
    const [enableFtpSettings, setEnableFtpSettings] = useState(false);

    const formRef = useRef<FormContainerRef>(null);

    useEffect(() => {
        setFormData({
            id: item.id,
            organisationId: item.organisationId,
            name: item.name,
            activeLicenses: item.activeLicenses,
            recycleBinRetentionDays: item.recycleBinRetentionDays,
            deviceDisconnectNotificationRecipients: item.deviceDisconnectNotificationRecipients,
            ftpSettings: item.ftpSettings,
            fastLanerOrgId: item.fastLanerOrgId
        });

        let selectedOptions: any[] = [];
        if (item.activeLicenses && item.activeLicenses.length > 0) {
            selectedOptions = LICENSE_OPTIONS.filter((option) => item.activeLicenses?.includes(option.license));
        }

        setSelectedLicenses(selectedOptions.map((option) => option.license));

        setEnableFtpSettings(
            !!item.ftpSettings &&
                (!!item.ftpSettings.host ||
                    !!item.ftpSettings.username ||
                    !!item.ftpSettings.password ||
                    !!item.ftpSettings.port)
        );
    }, [item]);

    const handleFieldChange = (field: keyof OrganisationInput, value: any) => {
        setFormData((prev) => ({ ...prev, [field]: value }));
    };

    const handleLicenseChange = (e: React.ChangeEvent<HTMLInputElement>, license: ActiveLicenses) => {
        const checkedLicenses = e.target.checked
            ? [...selectedLicenses, license]
            : selectedLicenses.filter((id) => id !== license);

        setSelectedLicenses(checkedLicenses);

        setFormData((prev) => ({
            ...prev,
            activeLicenses: checkedLicenses
        }));
    };

    const handleSave = () => {
        const dataToSave = {
            ...formData,
            ftpSettings: enableFtpSettings ? formData.ftpSettings : null
        };

        const isValid = formRef.current?.validate?.() ?? formValid;
        if (isValid) {
            onSave(dataToSave);
        } else {
            notifyWarning('invalidInput');
        }
    };

    if (isSaving) {
        return (
            <Box className="editor-loading">
                <CircularProgress />
            </Box>
        );
    }

    return (
        <Box className="editor-container">
            <Box className="editor-sidebar-actions">
                <EditorActions
                    onSave={handleSave}
                    onCancel={onCancel}
                    onDelete={() => onDelete(item)}
                    saveDisabled={!formValid || !formData.name?.trim() || isSaving}
                    showDelete={!!formData.id}
                />
            </Box>

            <Box className="editor-sidebar-content organisations-editor-content">
                <FormContainer formRef={formRef} value={formValid} onChange={setFormValid}>
                    <Box className="editor-form-container organisations-editor-container">
                        <div className="form-row">
                            <div className="form-col form-col-12">
                                <FormTextField
                                    name="name"
                                    label={`${t('name')} *`}
                                    value={formData.name || ''}
                                    onChange={(e) => handleFieldChange('name', e.target.value)}
                                    required
                                    fullWidth
                                    rules={[required(t)]}
                                    FormHelperTextProps={{ className: 'helper-text-min-height' }}
                                />
                            </div>
                        </div>

                        <div className="form-row">
                            <div className="form-col form-col-12">
                                <Typography gutterBottom className="license-section-title">
                                    {t('licenses')} *
                                </Typography>
                                <Box className="license-selection-grid">
                                    {LICENSE_OPTIONS.map((option) => (
                                        <Box key={option.license} className="license-option">
                                            <WcnCheckbox
                                                id={`license-${option.license}`}
                                                checked={selectedLicenses.includes(option.license)}
                                                onChange={(e) => handleLicenseChange(e, option.license)}
                                                label={option.label}
                                                size="small"
                                            />
                                        </Box>
                                    ))}
                                </Box>
                            </div>
                        </div>

                        <div className="form-row">
                            <div className="form-col form-col-12 form-col-6">
                                <FormTextField
                                    name="recycleBinRetentionDays"
                                    label={t('recycleBinRetentionDays')}
                                    type="number"
                                    value={formData.recycleBinRetentionDays?.toString() || ''}
                                    onChange={(e) => {
                                        const value = e.target.value;
                                        handleFieldChange(
                                            'recycleBinRetentionDays',
                                            value === '' ? undefined : parseInt(value, 10)
                                        );
                                    }}
                                    fullWidth
                                    FormHelperTextProps={{ className: 'helper-text-min-height' }}
                                    rules={[notNegative(t)]}
                                />
                            </div>
                            <div className="form-col form-col-12 form-col-6">
                                <FormTextField
                                    name="fastLanerOrgId"
                                    label={t('fastLanerOrgId')}
                                    value={formData.fastLanerOrgId || ''}
                                    onChange={(e) => handleFieldChange('fastLanerOrgId', e.target.value)}
                                    fullWidth
                                    FormHelperTextProps={{ className: 'helper-text-min-height' }}
                                />
                            </div>
                        </div>

                        <div className="form-row">
                            <div className="form-col form-col-12">
                                <FormTextField
                                    name="deviceDisconnectNotificationRecipients"
                                    label={t('deviceDisconnectNotificationRecipients')}
                                    value={formData.deviceDisconnectNotificationRecipients?.join(', ') || ''}
                                    onChange={(e) =>
                                        handleFieldChange(
                                            'deviceDisconnectNotificationRecipients',
                                            e.target.value
                                                .split(',')
                                                .map((s) => s.trim())
                                                .filter(Boolean)
                                        )
                                    }
                                    helperText={t('enterEmailsSeperatedByCommas')}
                                    fullWidth
                                    FormHelperTextProps={{ className: 'helper-text-italic' }}
                                />
                            </div>
                        </div>

                        <div className="form-row">
                            <div className="form-col form-col-12">
                                <Typography variant="h6" className="editor-section-header ftp-settings-header">
                                    <Switch
                                        checked={enableFtpSettings}
                                        onChange={(e) => {
                                            const enabled = e.target.checked;
                                            setEnableFtpSettings(enabled);
                                            if (enabled) {
                                                handleFieldChange('ftpSettings', {
                                                    username: '',
                                                    password: '',
                                                    host: '',
                                                    port: undefined
                                                });
                                            } else {
                                                handleFieldChange('ftpSettings', null);
                                            }
                                        }}
                                        size="small"
                                        sx={{ mr: 1 }}
                                    />
                                    {t('ftpSettings')}
                                </Typography>
                            </div>
                        </div>

                        {enableFtpSettings && (
                            <>
                                <div className="form-row">
                                    <div className="form-col form-col-12 form-col-6">
                                        <FormTextField
                                            name="ftpHost"
                                            label={t('ftpHost')}
                                            value={formData.ftpSettings?.host || ''}
                                            onChange={(e) => {
                                                const currentSettings = formData.ftpSettings || {};
                                                handleFieldChange('ftpSettings', {
                                                    ...currentSettings,
                                                    host: e.target.value
                                                });
                                            }}
                                            fullWidth
                                            FormHelperTextProps={{
                                                className: 'helper-text-min-height'
                                            }}
                                        />
                                    </div>
                                    <div className="form-col form-col-12 form-col-6">
                                        <FormTextField
                                            name="ftpPort"
                                            label={t('ftpPort')}
                                            type="number"
                                            value={formData.ftpSettings?.port?.toString() || ''}
                                            onChange={(e) => {
                                                const value = e.target.value;
                                                const currentSettings = formData.ftpSettings || {};
                                                handleFieldChange('ftpSettings', {
                                                    ...currentSettings,
                                                    port: value === '' ? undefined : Number.parseInt(value, 10)
                                                });
                                            }}
                                            fullWidth
                                            FormHelperTextProps={{
                                                className: 'helper-text-min-height'
                                            }}
                                            rules={[notNegative(t)]}
                                        />
                                    </div>
                                </div>

                                <div className="form-row">
                                    <div className="form-col form-col-12 form-col-6">
                                        <FormTextField
                                            name="ftpUsername"
                                            label={t('ftpUsername')}
                                            value={formData.ftpSettings?.username || ''}
                                            onChange={(e) => {
                                                const currentSettings = formData.ftpSettings || {};
                                                handleFieldChange('ftpSettings', {
                                                    ...currentSettings,
                                                    username: e.target.value
                                                });
                                            }}
                                            fullWidth
                                            FormHelperTextProps={{
                                                className: 'helper-text-min-height'
                                            }}
                                        />
                                    </div>
                                    <div className="form-col form-col-12 form-col-6">
                                        <FormTextField
                                            name="ftpPassword"
                                            label={t('ftpPassword')}
                                            type={showPassword ? 'text' : 'password'}
                                            value={formData.ftpSettings?.password || ''}
                                            onChange={(e) => {
                                                const currentSettings = formData.ftpSettings || {};
                                                handleFieldChange('ftpSettings', {
                                                    ...currentSettings,
                                                    password: e.target.value
                                                });
                                            }}
                                            fullWidth
                                            FormHelperTextProps={{
                                                className: 'helper-text-min-height'
                                            }}
                                            InputProps={{
                                                endAdornment: (
                                                    <InputAdornment position="end">
                                                        <IconButton
                                                            aria-label={
                                                                showPassword
                                                                    ? 'password visible, click to hide'
                                                                    : 'password hidden, click to show'
                                                            }
                                                            onClick={() => setShowPassword(!showPassword)}
                                                            onMouseDown={(event) => event.preventDefault()}
                                                            edge="end"
                                                            size="small"
                                                        >
                                                            {showPassword ? (
                                                                <i className="bi bi-eye" />
                                                            ) : (
                                                                <i className="bi bi-eye-slash" />
                                                            )}
                                                        </IconButton>
                                                    </InputAdornment>
                                                )
                                            }}
                                        />
                                    </div>
                                </div>
                            </>
                        )}
                    </Box>
                </FormContainer>
            </Box>

            {isSaving && (
                <Backdrop open={isSaving} className="editor-backdrop">
                    <CircularProgress color="inherit" />
                </Backdrop>
            )}
        </Box>
    );
};

export default OrganisationEditor;
